"use client";

import { useAdmin } from "@/lib/admin/context";
import { AdminPanelLink } from "./partials/AdminPanelLink";
import { TabCatalog } from "./partials/TabCatalog";
import { EditModeToggle } from "./partials/EditModeToggle";
import { AdminLocaleSwitch } from "./partials/AdminLocaleSwitch";
import { NotificationsBell } from "./partials/NotificationsBell";
import { UserMenuDropdown } from "./partials/UserMenuDropdown";
import { DevBannerToggle } from "./partials/DevBannerToggle";
import { CatalogAdminToolbar } from "@/components/admin/CatalogAdminToolbar/CatalogAdminToolbar";
import { LayoutAndWindowsPanel } from "@/components/admin/LayoutAndWindowsPanel/LayoutAndWindowsPanel";
import styles from "./AdminTopBar.module.css";

/**
 * AdminTopBar — sticky top bar dla zalogowanego admina.
 *
 * Layout (od lewej):
 *   1. **Panel administracyjny Imperial** ↗ (link do classic IADMIN, nowa karta)
 *   2. Tab "Katalog" (toggle dropdown `<CatalogAdminToolbar />`)
 *   3. **"Zarządzaj / Zamknij panel"** (`EditModeToggle`) — toggle dropdown
 *      `<LayoutAndWindowsPanel />` (drag&drop okien + wybór layoutu).
 *   ─── spacer ───
 *   4. 🌐 AdminLocaleSwitch — wersja językowa którą admin aktualnie zarządza
 *   5. 🔔 Notyfikacje (badge + dropdown)
 *   6. 👤 Admin ▼ (user menu)
 *
 * **Po unifikacji visit page:** strona zawsze renderuje się jako visitor view
 * (`WindowAreaView` z rzeczywistym contentem). Admin nie ma osobnego edit
 * canvas — edycja przez drop-down panel `LayoutAndWindowsPanel` (overlay nad
 * visitor view). Widoczność panelu zarządzania zależy WYŁĄCZNIE od
 * `activeTab === 'layoutAndWindows'` (toggle przez `EditModeToggle`).
 *
 * **CSS isolation:** `<div data-imperial-admin>` namespace; wszystkie tokens
 * `--admin-*` scoped przez selector — nie wyciekają do strony klienta.
 *
 * **ThemeToggle (light/dark/hc)** w `<HDefault />` (prawa strona nagłówka).
 *
 * Visibility: `cms:edit` scope wymagany — top bar renderowany TYLKO dla
 * adminów uprawnionych do edycji.
 */
export function AdminTopBar() {
  const { user, hasScope, activeTab } = useAdmin();

  if (!user || !hasScope("cms:edit")) {
    return null;
  }

  /* LayoutAndWindowsPanel widoczny gdy `activeTab === 'layoutAndWindows'`
     (toggle przez EditModeToggle). Visit page renderuje się identycznie dla
     admina/visitor'a, panel jest overlayem nad nią. */
  const showLayoutPanel = activeTab === "layoutAndWindows";

  return (
    <div data-imperial-admin>
      <div className={styles.root}>
        <div className={styles.left}>
          <AdminPanelLink />
          <span className={styles.divider} aria-hidden="true" />
          <TabCatalog />
          <EditModeToggle />
        </div>

        <div className={styles.right}>
          {/* Locale switch — pokazuje którą wersją językową admin aktualnie
              zarządza, dropdown z możliwością przełączenia. Każdy język to
              osobna "strona" CMS (układ + okna + konfiguracja per-language).

              Język UI panelu (PL/EN tłumaczeń stringów admin'a) NIE jest tutaj
              przełączany — czytamy go read-only z `$_SESSION['sa_lang']` przez
              SSO bridge i propagujemy do `useAdmin().adminLocale`. Zmiana
              przez classic IADMIN > górny pasek > wybór języka. */}
          <AdminLocaleSwitch />
          <span className={styles.divider} aria-hidden="true" />
          <NotificationsBell />
          <UserMenuDropdown />
          {/* Dev banner toggle — widoczny tylko w NODE_ENV=development.
              Po prawej stronie od informacji o logowaniu (UserMenuDropdown). */}
          <DevBannerToggle />
        </div>
      </div>

      {activeTab === "catalog" && <CatalogAdminToolbar />}
      {showLayoutPanel && <LayoutAndWindowsPanel />}
    </div>
  );
}
